import { styled } from "goober";
import { type ReactNode, useState } from "react";
import { formatBytes } from "../../lib/formatBytes";
import { ExecutionSidebarRow, Sidebar } from "../ExecutionSidebar";
import { Dropdown } from "../Dropdown";
import { Icon, type IconName } from "../Icon";
import { SubtleButton } from "../SubtleButton";
import type { StorageUsageLevel } from "./storageUsageLevel";

export type StorageAction = { label: string; icon: IconName } & (
  | { to: string }
  | { onPress: () => void }
);

// Holds a row and its actions as siblings, since buttons can't nest inside
// the row's link.
const RowFrame = styled("div")`
  position: relative;
  flex-shrink: 0;

  &:hover [data-row-menu],
  & [data-row-menu]:focus-within,
  &[data-menu-open="true"] [data-row-menu] {
    opacity: 1;
    pointer-events: auto;
  }

  /* The menu renders outside the row, so an open menu keeps the row's hover. */
  &[data-menu-open="true"] > :first-child,
  &[data-highlighted="true"] > :first-child {
    background: var(--alpha4);
  }

  /* Truncates the row's text short of the menu button while it shows. */
  &[data-has-menu="true"]:hover > :first-child,
  &[data-menu-open="true"] > :first-child {
    padding-right: 36px;
  }
`;

// Insets the title to the main header’s right edge and the rows’ icons.
const Inset = styled("div")`
  display: contents;

  & > aside > :first-child {
    padding-left: 12px;
  }
`;

const RowMenu = styled("div")`
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--motion-fast) var(--ease-out);
`;

function formatShare(bytes: number, total: number): string {
  if (total === 0) return "–";
  const percent = (bytes / total) * 100;
  return percent < 1 ? "<1%" : `${Math.round(percent)}%`;
}

export function StorageSidebar({
  title,
  level,
  tileUrl,
  tileActions,
  tileNote,
  tileIcon,
  isHighlighted,
  onHover,
  onDrill,
}: {
  title: string;
  level: StorageUsageLevel;
  tileUrl: (key: string) => string | undefined;
  tileActions: (key: string) => StorageAction[];
  tileNote: (key: string) => string | undefined;
  tileIcon: (key: string) => ReactNode;
  isHighlighted: (key: string) => boolean;
  onHover: (key: string | null) => void;
  onDrill: (key: string) => void;
}) {
  const [menuKey, setMenuKey] = useState<string | null>(null);
  return (
    <Inset>
      <Sidebar title={title}>
        {level.tiles.map((tile) => {
          const actions = tileActions(tile.key);
          const details = [
            formatBytes(tile.bytes),
            formatShare(tile.bytes, level.totalBytes),
            tileNote(tile.key),
          ].filter(Boolean);
          return (
            <RowFrame
              key={tile.key}
              data-has-menu={actions.length > 0}
              data-menu-open={menuKey === tile.key}
              data-highlighted={isHighlighted(tile.key)}
              onMouseEnter={() => onHover(tile.key)}
              onMouseLeave={() => onHover(null)}
            >
              <ExecutionSidebarRow
                to={tileUrl(tile.key)}
                selected={false}
                label={tile.label}
                icon={tileIcon(tile.key)}
                onNavigate={() => onDrill(tile.key)}
              >
                {details.join(" · ")}
              </ExecutionSidebarRow>
              {actions.length > 0 && (
                <RowMenu data-row-menu>
                  <Dropdown
                    opener={
                      <SubtleButton
                        icon="more"
                        variant="secondary"
                        aria-label={`Actions for ${tile.label}`}
                      />
                    }
                    align="bottom"
                    minWidth={180}
                    onOpenChange={(open) =>
                      setMenuKey((current) =>
                        open ? tile.key : current === tile.key ? null : current
                      )
                    }
                    sections={[
                      {
                        items: actions.map((action) => ({
                          key: action.label,
                          left: <Icon name={action.icon} size={16} />,
                          label: action.label,
                          ...("to" in action
                            ? { type: "link" as const, to: action.to }
                            : {
                                type: "button" as const,
                                onClick: action.onPress,
                              }),
                        })),
                      },
                    ]}
                  />
                </RowMenu>
              )}
            </RowFrame>
          );
        })}
      </Sidebar>
    </Inset>
  );
}
